<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import VText from "../VText/VText.vue";
import VLoader from "../VLoader/VLoader.vue";
import SuccessIcon from "@magnit/icons/src/assets/icons/success-check-24.svg";

import useSelectionControl, {
  type ISelectionControlEmit,
} from "../../composables/useSelectionControl";
import type { ISwitchProps, ISwitchEmits } from "./VSwitch.types";

const props = withDefaults(defineProps<ISwitchProps>(), {
  wrapperClass: undefined,
  pending: false,
  label: "",
  value: true,
});

const emit = defineEmits<ISwitchEmits>();
defineOptions({ inheritAttrs: false });

const { onChange, isChecked } = useSelectionControl(
  props,
  emit as ISelectionControlEmit,
);

let isDisabled = computed(() => props.disabled || props.pending);

const pressAnimation = ref(false);
const switchPressAnimation = (enable?: boolean) => {
  pressAnimation.value = Boolean(enable);
};

const isIcon = computed(
  () =>
    props.icon && !props.pending && isChecked.value && !pressAnimation.value,
);

// флаг добавляет анимацию "bounced" для "бегунка" при его движении
// и решает проблему ненужного воспроизведения анимации при загрузке компонента
const hasAnimation = ref(false);
const unWatch = watch(isChecked, () => {
  hasAnimation.value = true;
  unWatch();
});
</script>

<template>
  <div :class="wrapperClass" class="pl-switch">
    <label
      class="pl-switch__label"
      :class="{
        'pl-switch__label_disabled': disabled,
        'pl-switch__label_pending': pending,
      }"
    >
      <input
        :checked="isChecked"
        :disabled="isDisabled"
        class="pl-switch__field"
        type="checkbox"
        v-bind="$attrs"
        @change="onChange"
        @mousedown.stop="switchPressAnimation(true)"
        @mouseup="switchPressAnimation()"
        @mouseleave="switchPressAnimation()"
      />
      <span class="pl-switch__area">
        <span
          :class="{
            'pl-switch__thumb__with-animation': hasAnimation && !isDisabled,
            'pl-switch__thumb__press': pressAnimation && !isDisabled,
          }"
          class="pl-switch__thumb"
        >
          <span v-show="pending" class="pl-switch__thumb_loader-wrapper">
            <VLoader size="xs" color="project" />
          </span>
          <span v-if="isIcon" class="pl-switch__thumb_checked-wrapper">
            <SuccessIcon />
          </span>
        </span>
      </span>
      <VText v-if="label" class="pl-switch__text">
        {{ label }}
      </VText>
    </label>
  </div>
</template>

<style lang="postcss" scoped>
.pl-switch {
  align-items: center;
  display: flex;
  justify-content: flex-start;
}

.pl-switch__label {
  position: relative;
  display: inline-flex;
  vertical-align: top;
  cursor: pointer;

  &_disabled {
    cursor: not-allowed;
  }

  &_pending {
    cursor: progress;
  }
}

.pl-switch__field {
  position: absolute;
  border: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  opacity: 0;
  outline: none;
  z-index: 1;
  border-radius: 32px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  .pl-switch__label_pending & {
    cursor: progress;
  }
}

.pl-switch__area {
  display: flex;
  position: relative;
  width: 50px;
  height: 32px;
  align-items: center;
  background-color: var(--pl-push-control-background-off-default);
  border-radius: 32px;
  transition: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: background, border;

  .pl-switch__field:hover + & {
    background-color: var(--pl-push-control-background-off-hover);
  }

  .pl-switch__field:focus-visible + & {
    outline: 2px solid var(--pl-tabulation-border-focused);
    outline-offset: 2px;
  }

  .pl-switch__field:active + & {
    background-color: var(--pl-push-control-background-off-pressed);
  }

  .pl-switch__field[disabled] + & {
    background-color: var(--pl-push-control-background-off-disabled);
  }

  .pl-switch__field:checked + & {
    background-color: var(--pl-push-control-background-on-default);
    color: var(--pl-icon-primary);
  }

  .pl-switch__field:checked:hover + & {
    background-color: var(--pl-push-control-background-on-hover);
  }

  .pl-switch__field:checked:active + & {
    background-color: var(--pl-push-control-background-on-pressed);
  }

  .pl-switch__field:checked[disabled] + & {
    background-color: var(--pl-push-control-background-on-disabled);
    color: var(--pl-push-control-background-on-disabled);
  }
}

.pl-switch__thumb {
  position: absolute;
  height: calc(100% - 4px);
  background-color: var(--pl-push-control-fill-check);
  border-radius: var(--pl-unit-x8);
  transition: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: left, transform;
  aspect-ratio: 1;
  transform: translateX(0);
  left: 2px;
  display: flex;
  justify-content: center;
  align-items: center;

  .pl-switch__field:checked + .pl-switch__area & {
    left: calc(100% - 2px);
    transform: translateX(-100%);
  }

  &__with-animation {
    animation-name: expand-switcher-rev;
    animation-duration: 0.25s;

    .pl-switch__field:checked + .pl-switch__area & {
      animation-name: expand-switcher;
      animation-duration: 0.25s;
    }
  }

  &__press {
    transition: aspect-ratio 0.25s;
    aspect-ratio: 1.2;
  }

  &_loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    aspect-ratio: 1;
  }

  &_checked-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
}

.pl-switch__text {
  display: flex;
  padding-top: var(--pl-unit-x1);
  flex: 1 0 0;

  .pl-switch & {
    padding-left: var(--pl-unit-x2);
  }
}

@keyframes expand-switcher {
  from {
    aspect-ratio: 1.2;
  }

  to {
    aspect-ratio: 1;
  }
}

@keyframes expand-switcher-rev {
  from {
    aspect-ratio: 1.2;
  }

  to {
    aspect-ratio: 1;
  }
}
</style>
